import { Injectable, inject, isDevMode } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';

// ✅ Native Google Sign-In (Android/iOS) → tokens → signInWithCredential()
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { AppUser } from './auth.service.types';

// 👇 UID admin
import { ADMIN_UID } from '../config/admin.config';

// ✅ Firebase Analytics (GA4)
import { getAnalytics, logEvent, setUserId } from 'firebase/analytics';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);

  // ✅ Flag admin centralizado
  private _isAdmin$ = new BehaviorSubject<boolean>(false);
  /** Observable con el estado admin */
  readonly isAdmin$ = this._isAdmin$.asObservable();

  constructor() {
    // Mantén un listener global para actualizar el flag admin
    onAuthStateChanged(this.auth, (fbUser) => {
      const isAdmin = !!fbUser && fbUser.uid === ADMIN_UID;
      this._isAdmin$.next(isAdmin);
    });
  }

  /** Getter síncrono del estado admin (útil en guards/plantillas) */
  isAdmin(): boolean {
    return this._isAdmin$.value;
  }

  // =======================
  // ✅ Analytics helpers
  // =======================

  private getAnalyticsSafe() {
    if (typeof window === 'undefined') return null;
    try {
      return getAnalytics();
    } catch {
      return null;
    }
  }

  private logAnalytics(eventName: string, params: Record<string, any> = {}) {
    const a = this.getAnalyticsSafe();
    if (!a) return;

    const payload: Record<string, any> = { ...params };
    // ✅ debug_mode SOLO en dev (y sin TS4111)
    if (isDevMode()) payload['debug_mode'] = true;

    try {
      logEvent(a, eventName, payload);
    } catch {
      // no rompemos nada si analytics no está disponible/bloqueado
    }
  }

  private setAnalyticsUser(uid: string | null) {
    const a = this.getAnalyticsSafe();
    if (!a) return;

    try {
      setUserId(a, (uid ?? null) as any);
    } catch {
      // ignore
    }
  }

  // =======================
  // ✅ Login (web + native)
  // =======================

  /**
   * ✅ Login con Google “inteligente”
   * - Web: popup
   * - Android/iOS: @capacitor-firebase/authentication → idToken/accessToken → signInWithCredential
   *
   * ⚠️ NO navega. (La navegación la decides tú desde LoginPage u otro sitio)
   */
  async loginWithGoogle(): Promise<AppUser> {
    const platform = Capacitor.getPlatform();
    console.log('[Auth] loginWithGoogle platform:', platform);

    if (platform === 'android' || platform === 'ios') {
      return this.loginWithGoogleNative();
    }

    return this.loginWithGooglePopup();
  }

  /**
   * 🔹 Mantengo tu nombre antiguo por compatibilidad
   */
  async loginWithPopup(): Promise<AppUser> {
    return this.loginWithGooglePopup();
  }

  /**
   * 🔹 Web: Login con Google (popup)
   */
  private async loginWithGooglePopup(): Promise<AppUser> {
    try {
      console.log('[Auth] Web Google login (popup) start');
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);
      console.log('[Auth] Web Google login (popup) success:', credential.user?.uid);
      return await this.afterLogin(credential.user, 'google_popup');
    } catch (error) {
      console.error('[Auth] Web Google login (popup) failed:', error);
      throw error;
    }
  }

  /**
   * 🔹 Android/iOS: Login nativo con Google (Capacitor)
   *    → obtenemos tokens del plugin
   *    → los convertimos en credencial JS
   *    → signInWithCredential() para que AngularFire tenga la sesión
   */
  private async loginWithGoogleNative(): Promise<AppUser> {
    try {
      console.log('[Auth] Native Google login start');
      const result: any = await FirebaseAuthentication.signInWithGoogle();
      console.log('[Auth] Native Google login result keys:', Object.keys(result || {}));

      const idToken =
        result?.credential?.idToken ??
        result?.idToken ??
        result?.authentication?.idToken ??
        null;

      const accessToken =
        result?.credential?.accessToken ??
        result?.accessToken ??
        result?.authentication?.accessToken ??
        null;

      console.log('[Auth] Native Google login tokens:', {
        hasIdToken: !!idToken,
        hasAccessToken: !!accessToken,
      });

      if (!idToken && !accessToken) {
        throw new Error(
          'Google native login no devolvió idToken/accessToken. Revisa la config de @capacitor-firebase/authentication.'
        );
      }

      const cred = GoogleAuthProvider.credential(
        idToken || undefined,
        accessToken || undefined
      );

      const userCred = await signInWithCredential(this.auth, cred);
      console.log('[Auth] Native Google signInWithCredential ok:', userCred.user?.uid);
      return await this.afterLogin(userCred.user, 'google_native');
    } catch (error) {
      console.error('[Auth] Native Google login failed:', error);
      throw error;
    }
  }

  /**
   * 🔹 Login con Google (redirect) — web
   */
  async loginWithRedirect(): Promise<void> {
    const provider = new GoogleAuthProvider();

    // (Opcional pero útil) Evento de inicio de login
    this.logAnalytics('login_start', { method: 'google_redirect' });

    await signInWithRedirect(this.auth, provider);
  }

  /**
   * 🔹 Manejar el resultado del redirect (web)
   * ⚠️ NO navega aquí.
   */
  async handleRedirectResult(): Promise<AppUser | null> {
    const result = await getRedirectResult(this.auth);
    if (!result?.user) return null;

    const appUser = await this.afterLogin(result.user, 'google_redirect');
    return appUser;
  }

  /**
   * ✅ Post-login común:
   * - analytics userId
   * - init userProgress
   * - ensure users/{uid} (incl. city null)
   * - eventos GA4
   * - flag admin
   */
  private async afterLogin(
    fbUser: FirebaseUser,
    method: 'google_popup' | 'google_redirect' | 'google_native'
  ): Promise<AppUser> {
    const appUser = this.mapFirebaseUser(fbUser);

    // ✅ Analytics: asociar uid cuanto antes
    this.setAnalyticsUser(appUser.uid);

    // 🧩 Inicializar datos de progreso (userProgress)
    const createdProgress = await this.initializeUserData(appUser);

    // 🧩 Asegurar perfil básico (users/{uid}) y saber si falta ciudad
    const profileResult =
      await this.ensureUserProfileAndReturnNeedsLocation(appUser);

    const isNewUser = profileResult.createdProfile || createdProgress;

    // ✅ Analytics: eventos recomendados GA4
    if (isNewUser) {
      this.logAnalytics('sign_up', { method });
    }
    this.logAnalytics('login', {
      method,
      is_new_user: isNewUser ? 1 : 0,
      needs_location: profileResult.needsLocation ? 1 : 0,
    });

    // Actualiza flag admin tras login
    this._isAdmin$.next(appUser.uid === ADMIN_UID);

    return appUser;
  }

  /**
   * 🔴 Cerrar sesión (con manejo de errores)
   */
  async logout(): Promise<void> {
    try {
      // Evento logout antes de limpiar
      this.logAnalytics('logout', { method: 'app' });

      // 1) Cerrar sesión en Firebase JS (AngularFire)
      await signOut(this.auth);

      // 2) Si es native, cerramos también el estado nativo (por si acaso)
      const platform = Capacitor.getPlatform();
      if (platform === 'android' || platform === 'ios') {
        try {
          await FirebaseAuthentication.signOut();
        } catch {
          // no rompemos logout si el plugin falla
        }
      }

      this._isAdmin$.next(false); // limpia flag admin

      // ✅ Analytics: limpiar userId
      this.setAnalyticsUser(null);

      console.log('✅ Sesión cerrada correctamente');
    } catch (error) {
      console.error('❌ Error al cerrar sesión:', error);
      throw error;
    }
  }

  /**
   * 🔁 Observable de cambios de estado del usuario
   */
  onAuthStateChanged$(): Observable<AppUser | null> {
    return new Observable<AppUser | null>((observer) => {
      const unsubscribe = onAuthStateChanged(this.auth, (fbUser) => {
        observer.next(fbUser ? this.mapFirebaseUser(fbUser) : null);
      });
      return { unsubscribe };
    });
  }

  /**
   * 👤 Usuario actual (si existe)
   */
  getCurrentUser(): AppUser | null {
    const user = this.auth.currentUser;
    return user ? this.mapFirebaseUser(user) : null;
  }

  /**
   * 🔧 Mapeo de FirebaseUser a AppUser
   */
  private mapFirebaseUser(user: FirebaseUser): AppUser {
    return {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      photoURL: user.photoURL || undefined,
    };
  }

  /**
   * 🧩 Inicializar datos del usuario en Firestore (progreso)
   *     Colección: userProgress/{uid}
   *
   * ✅ Devuelve si se creó por primera vez (para medir sign_up)
   */
  private async initializeUserData(user: AppUser): Promise<boolean> {
    const userRef = doc(this.firestore, `userProgress/${user.uid}`);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        streak: 0,
        coins: 0,
        lastCompletedDate: null,
        joinedAt: new Date(),
      });
      console.log(`🆕 Progreso inicial creado para ${user.displayName}`);
      return true;
    } else {
      console.log(`✅ Usuario ${user.displayName} ya tenía progreso`);
      return false;
    }
  }

  /**
   * 🧩 Asegurar perfil básico en `users/{uid}`
   *     y devolver si necesita onboarding de ubicación (city vacía)
   *
   * ✅ También devuelve si el perfil se creó por primera vez (para sign_up)
   */
  private async ensureUserProfileAndReturnNeedsLocation(
    user: AppUser
  ): Promise<{ needsLocation: boolean; createdProfile: boolean }> {
    const profileRef = doc(this.firestore, `users/${user.uid}`);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
      // Crear perfil básico con city/province/country a null
      await setDoc(profileRef, {
        uid: user.uid,
        email: user.email || null,
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        provider: 'google',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        city: null,
        province: null,
        country: null,
      });

      console.log(`🆕 Perfil básico creado en users/${user.uid}`);
      // No tiene ciudad aún → necesitaría onboarding (pero NO lo forzamos aquí)
      return { needsLocation: true, createdProfile: true };
    } else {
      const data: any = profileSnap.data();

      // ✅ si faltan campos básicos, los rellenamos (merge) sin pisar lo demás
      const patch: any = { updatedAt: serverTimestamp() };
      let shouldPatch = false;

      if ((!data?.email || data.email === '') && user.email) {
        patch.email = user.email;
        shouldPatch = true;
      }

      if ((!data?.displayName || data.displayName === '') && user.displayName) {
        patch.displayName = user.displayName;
        shouldPatch = true;
      }

      if ((!data?.photoURL || data.photoURL === '') && user.photoURL) {
        patch.photoURL = user.photoURL;
        shouldPatch = true;
      }

      if (shouldPatch) {
        await setDoc(profileRef, patch, { merge: true });
        console.log(
          `🛠️ Perfil actualizado (campos faltantes) en users/${user.uid}`
        );
      }

      const needsLocation = !data?.city;
      console.log(
        `✅ Perfil encontrado en users/${user.uid}. needsLocation = ${needsLocation}`
      );

      return { needsLocation, createdProfile: false };
    }
  }
}

// 👇 exportamos también AppUser
export type { AppUser } from './auth.service.types';
